import React from 'react';
import get from 'lodash/get';
import defaults from 'lodash/defaults';
import has from 'lodash/has';

import { PAGE, SECTION_COLUMN, CONTAINER, CAROUSEL_CARD } from '../../../constants/widgetTypes';

const FLEX_VALUE_TO_CSS = {
  flexStart: 'flex-start',
  flexEnd: 'flex-end',
  spaceBetween: 'space-between',
  spaceAround: 'space-around',
  spaceEvenly: 'space-evenly',
  rowReverse: 'row-reverse',
  columnReverse: 'column-reverse',
  wrapReverse: 'wrap-reverse',
};

export function flexValueToCss(value) {
  return get(FLEX_VALUE_TO_CSS, value, value);
}

export function pxToNumber(value) {
  if (typeof value === 'number') return value;
  return parseInt(value, 10) || 0;
}

export function numberToPx(value) {
  return `${pxToNumber(value)}px`;
}

export const DEFAULT_FLEX_LAYOUT = {
  flexDirection: 'row',
  justifyContent: 'flexStart',
  alignItems: 'flexStart',
  alignContent: 'flexStart',
  flexWrap: 'wrap',
  gap: '0px',
  padding: { topLeft: '0px', topRight: '0px', bottomLeft: '0px', bottomRight: '0px', lock: true },
  minHeight: null
};

export const PAGE_DEFAULT_FLEX_LAYOUT = {
  ...DEFAULT_FLEX_LAYOUT,
  flexDirection: 'column',
  alignItems: 'flexStart'
};

export const MOBILE_FLEX_DEFAULTS = {
  flexDirection: 'column'
};

export function resolveFlexLayout(widgetProps, breakpoint, baseDefaults = DEFAULT_FLEX_LAYOUT) {
  const desktop = get(widgetProps, 'properties.desktop.flexProperty');
  if (breakpoint === 'mobile') {
    const mobile = get(widgetProps, 'properties.mobile.flexProperty');
    return defaults({}, mobile, MOBILE_FLEX_DEFAULTS, desktop, baseDefaults);
  }
  return defaults({}, desktop, baseDefaults);
}

export function resolvePadding(padding) {
  if (typeof padding === 'number' || typeof padding === 'string') {
    const v = pxToNumber(padding);
    return { topLeft: v, topRight: v, bottomRight: v, bottomLeft: v, lock: true };
  }
  return {
    topLeft: pxToNumber(get(padding, 'topLeft', 0)),
    topRight: pxToNumber(get(padding, 'topRight', 0)),
    bottomRight: pxToNumber(get(padding, 'bottomRight', 0)),
    bottomLeft: pxToNumber(get(padding, 'bottomLeft', 0)),
    lock: get(padding, 'lock', false),
  };
}

export function paddingToCss(padding) {
  const p = resolvePadding(padding);
  return `${p.topLeft}px ${p.topRight}px ${p.bottomRight}px ${p.bottomLeft}px`;
}

export const FlexFlowContext = React.createContext({ flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT });

const FLEX_CONTAINERS = {
  [PAGE]: { baseDefaults: PAGE_DEFAULT_FLEX_LAYOUT },
  [SECTION_COLUMN]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CONTAINER]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CAROUSEL_CARD]: {baseDefaults: DEFAULT_FLEX_LAYOUT}
};

export function isFlexContainerType(type) {
  return has(FLEX_CONTAINERS, type);
}

export function flexBaseDefaults(type) {
  return get(FLEX_CONTAINERS, [type, 'baseDefaults'], DEFAULT_FLEX_LAYOUT);
}

export const LAYOUT_TYPE_FLEX = 'flex';
export const LAYOUT_TYPE_FREEFORM = 'freeform';

// layoutType is tri-state: 'flex' | 'freeform' | null (null = inherit the
// parent's behaviour).
//
// Pages saved before layoutType existed only carry the boolean flexBoxEnabled,
// so the rule is: if layoutType has a value, it decides; otherwise fall back to
// flexBoxEnabled (true -> 'flex', false -> 'freeform', missing -> null).
// "Has a value" means non-null -- the API sends layoutType: null on widgets
// that predate it, and that must not hide their flexBoxEnabled.
export function normalizeLayoutType(value) {
  if (value === LAYOUT_TYPE_FLEX || value === LAYOUT_TYPE_FREEFORM) return value;
  if (value === true) return LAYOUT_TYPE_FLEX;
  if (value === false) return LAYOUT_TYPE_FREEFORM;
  return null;
}

export function ownLayoutType(widgetProps) {
  const props = get(widgetProps, 'properties.desktop');
  if (!props) return null;
  return normalizeLayoutType(props.layoutType != null ? props.layoutType : props.flexBoxEnabled);
}

export function resolveFlexEnabled(widgetProps, parentFlex = false) {
  const own = ownLayoutType(widgetProps);
  if (own === null) return !!parentFlex;
  return own === LAYOUT_TYPE_FLEX;
}

export function flowsChildrenInFlex(type, widgetProps, parentFlex = false) {
  return isFlexContainerType(type) && resolveFlexEnabled(widgetProps, parentFlex);
}

export function allColumnsInFlexMode(columns = [], parentFlex = false) {
  return columns.length > 0
    && columns.every((column) => flowsChildrenInFlex(column.type, column, parentFlex));
}

export const CONTENT_DRIVEN_SECTION_HEIGHT = 'fit-content';

export const EMPTY_CONTENT_SECTION_MIN_HEIGHT = 200;

export const RESET_FLEX_CONTEXT = { flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT };

export function toCssDimension(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}
